import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { ShopState, FetchShops } from '../../../_state/shop/shop.state';
import { MarketingAccessService } from '../../../features/marketing/services/marketing-access.service';
import { AuthService } from '../auth.service';
import { RoutesEnum } from '../enums/routes.enum';

/**
 * Runtime per-shop gate for `/marketing` (canary). Layered AFTER
 * permissionsGuard. Compile-time `environment.enableMarketing` already decides
 * whether this route exists at all; this guard decides whether the CURRENT
 * active shop is in the runtime allowlist (backend MARKETING_ENABLED_SHOPS, via
 * GET /api/marketing/feature-access).
 *
 * Returns a UrlTree (not navigate+false) so the redirect is computed cleanly;
 * it redirects to /settings, which is itself ungated → no redirect loop.
 * Fail-closed: a missing shop or any access error denies. Demo sessions bypass
 * (matches permissionsGuard) since demo data has no per-shop allowlist.
 * (dashboardly-5aw14)
 */
export const marketingAccessGuard: CanActivateFn = async (
  _route,
  _state
): Promise<boolean | UrlTree> => {
  const store = inject(Store);
  const router = inject(Router);
  const access = inject(MarketingAccessService);
  const auth = inject(AuthService);

  // Demo sessions bypass per-shop gating (matches permissionsGuard).
  if (auth.isDemoMode()) return true;

  const deny = (): UrlTree => router.parseUrl(RoutesEnum.SETTINGS);

  // Ensure shops are loaded (hard reload / deep-link may hit an empty store).
  let active = store.selectSnapshot(ShopState.activeShop);
  if (!active) {
    try {
      await firstValueFrom(store.dispatch(new FetchShops()));
      active = store.selectSnapshot(ShopState.activeShop);
    } catch {
      return deny();
    }
  }
  if (!active?.id) return deny();

  const enabled = await firstValueFrom(access.isEnabled(active.id));
  return enabled ? true : deny();
};
